import { ethers } from "ethers"

// Provider for Ronin chain
const RONIN_RPC_URL = "https://api.roninchain.com/rpc"
const roninProvider = new ethers.JsonRpcProvider(RONIN_RPC_URL)

/**
 * Fetches NFTs from the Ronin chain for a given wallet address
 * @param walletAddress Ethereum wallet address
 * @returns Array of NFTs on Ronin chain
 */
export async function fetchRoninChainNFTs(
  walletAddress: string,
): Promise<Array<{ contractAddress: string; tokenId: string }>> {
  const tokenData: Array<{ contractAddress: string; tokenId: string }> = []

  try {
    // Convert Ethereum address to Ronin address format if needed
    const roninAddress = walletAddress.startsWith("ronin:") ? walletAddress : `ronin:${walletAddress.replace("0x", "")}`

    // Convert back to Ethereum format for provider calls
    const ethAddress = roninAddress.startsWith("ronin:") ? `0x${roninAddress.replace("ronin:", "")}` : walletAddress

    // List of known popular NFT contracts on Ronin
    const knownRoninNFTContracts = [
      "0x32950db2a7164ae833121501c797d79e7b79d74c", // Axie Infinity
      "0x47b5a7c2e4f07772696bbf8c8c32fe2b9eabd550", // Moki Genesis
      // Add more known Ronin NFT contracts here
    ]

    // Check each known contract for NFT ownership
    for (const contractAddress of knownRoninNFTContracts) {
      try {
        // Create a minimal ERC721 interface to check balance
        const erc721Interface = new ethers.Interface([
          "function balanceOf(address owner) view returns (uint256)",
          "function tokenOfOwnerByIndex(address owner, uint256 index) view returns (uint256)",
        ])

        const contract = new ethers.Contract(contractAddress, erc721Interface, roninProvider)

        // Check if the wallet owns any NFTs from this contract
        const balance = await contract.balanceOf(ethAddress)

        if (balance > 0) {
          // Wallet owns NFTs from this contract
          const balanceNumber = Number(balance)

          // Get token IDs (up to 10 per contract to avoid too many requests)
          const maxTokensToFetch = Math.min(balanceNumber, 10)

          for (let i = 0; i < maxTokensToFetch; i++) {
            try {
              const tokenId = await contract.tokenOfOwnerByIndex(ethAddress, i)
              tokenData.push({
                contractAddress: contractAddress.toLowerCase(),
                tokenId: tokenId.toString(),
              })
            } catch (error) {
              console.error(`Error fetching token ID for contract ${contractAddress}:`, error)
              // Continue with the next token
            }
          }
        }
      } catch (error) {
        console.error(`Error checking Ronin NFT contract ${contractAddress}:`, error)
        // Continue with the next contract
      }
    }

    // Log the results
    if (tokenData.length > 0) {
      console.log(`Found ${tokenData.length} NFTs on Ronin chain for wallet ${walletAddress}`)
    } else {
      console.log(`No NFTs found on Ronin chain for wallet ${walletAddress}`)
    }
  } catch (error) {
    console.error("Error fetching NFTs from Ronin chain:", error)
  }

  return tokenData
}
